import React, { ChangeEvent, FC, ReactNode, useCallback } from 'react';
import { useController } from 'react-hook-form';
import classNames from 'classnames';

import styles from './fieldStyles.scss';

const RadioField: FC<{
  disabled?: boolean;
  generateHelperElement?: (value: string) => ReactNode;
  label?: string;
  name: string;
  onChangeCallback?: (value: string) => void;
  options: {
    label: ReactNode | string;
    value: string;
  }[];
  size?: 'small' | 'medium' | 'large';
}> = ({
  disabled,
  generateHelperElement,
  label,
  name,
  onChangeCallback,
  options,
  size = 'medium'
}) => {
  const {
    field: { onChange, value, ...field },
    fieldState: { error },
    formState: { isSubmitting }
  } = useController({ name });

  const isDisabled = disabled || isSubmitting;

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedValue = event.target.value;

      onChange(selectedValue);
      if (onChangeCallback) onChangeCallback(selectedValue);
    },
    [onChange, onChangeCallback]
  );

  return (
    <div
      className={classNames(styles.radioField, {
        [styles.small]: size === 'small',
        [styles.medium]: size === 'medium',
        [styles.large]: size === 'large'
      })}
    >
      {label ? <label data-title>{label}</label> : null}
      <div className={styles.radioGroup}>
        {options.map(option => (
          <div key={option.value} className={styles.radioOption}>
            <input
              {...field}
              checked={value === option.value}
              disabled={isDisabled}
              id={`${name}-${option.value}`}
              onChange={handleOnChange}
              type="radio"
              value={option.value}
            />
            <label htmlFor={`${name}-${option.value}`}>{option.label}</label>
          </div>
        ))}
      </div>
      {error ? <label data-error>{error.message}</label> : null}
      {generateHelperElement ? generateHelperElement(value || '') : null}
    </div>
  );
};

export default RadioField;
